"use client";
import React, { useState, useMemo, useRef, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import CalcInputValues from "../ui/CalcInputValues";
import { formatCompactNumber, formatIndianNumber } from "@/utils/commonFunctions";
import { useIsMobile } from "@/utils/useIsMobile";
import CustomModal from "@/components/modal/CustomModal";
import Image from "next/image";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import StartSIPStrip from "../strips/StartSIPStrip";
import { allStpCalculation } from "@/utils/allCalculators";
import { useLoginModal } from "@/utils/useLoginModal";
import { AnimatePresence, motion } from "framer-motion";
import { ROOT_URL } from "@/utils/gloableVariables";

interface StpMonthReport {
  month: number;
  remainBalance: number;
  stpPerMonth: number;
  closingBalance: number;
  liquidGrowth: number;
  equityGrowth: number;
  equityTransferedValue: number;
  currentEquityValue: number;
}

interface StpCalculationResult {
  totalInvestment: number;
  stpPerMonth: number;
  liquidReturn: number;
  equityReturn: number;
  equityFutureValue: number;
  totalFutureValue: number;
  monthlyReport: StpMonthReport[];
  liquidAmtArr: number[];
  equityAmtArr: number[];
  monthArr: number[];
}

export default function StpCalc() {
  const [isOpen, setIsOpen] = useState(false);

  const [investmentAmount, setInvestmentAmount] = useState(100000);
  const [stpAmount, setStpAmount] = useState<string>("5000");
  const [months, setMonths] = useState(12);
  const [liquidRate, setLiquidRate] = useState(8);
  const [equityRate, setEquityRate] = useState(11);
  const [isCheckboxActive, setIsCheckboxActive] = useState(false);
  const [error, setError] = useState(false);

  const calcRef = useRef<HTMLDivElement>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  const { isLoggedIn, open, setModalKey } = useLoginModal();

  const [calculation, setCalculation] = useState<StpCalculationResult>();
  const isTab = useIsMobile(1025);
  const isMob = useIsMobile(769);

  const handleLogin = () => {
    open();
    setModalKey("stp-calculator");
  };

  useEffect(() => {
    const result = allStpCalculation(
      investmentAmount,
      Number(stpAmount),
      months,
      liquidRate,
      equityRate,
      isCheckboxActive,
    );
    setCalculation(result);
  }, [investmentAmount, months, liquidRate, equityRate, stpAmount, isCheckboxActive]);

  const handleExcelDownload = () => {
    if (!isLoggedIn) return handleLogin();
    const data =
      calculation?.monthlyReport.map((row) => ({
        month: row.month,
        remainBalance: Number(row.remainBalance).toFixed(2),
        stpPerMonth: Number(row.stpPerMonth).toFixed(2),
        liquidGrowth: Number(row.liquidGrowth).toFixed(2),
        equityGrowth: Number(row.equityGrowth).toFixed(2),
        currentEquityValue: Number(row.currentEquityValue).toFixed(2),
      })) ?? [];
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "STP Report");
    XLSX.writeFile(workbook, "Stp_Investment_Report.xlsx");
  };

  const handlePDFDownload = () => {
    if (!isLoggedIn) return handleLogin();
    const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "p" });
    const { pageSize } = pdf.internal;
    const pageWidth = pageSize.getWidth();
    const pageHeight = pageSize.getHeight();
    // Layout
    const layout = { marginX: 12, headerY: 12, titleY: 18, headerArea: 22, footer: 12, rowH: 10 };
    const usableWidth = pageWidth - layout.marginX * 2;
    // Headers + column widths
    const headers = ["Months", "Opening Balance", "STP Amount", "Liquid Returns", "Equity Returns", "Equity Value"];
    const colWidths = [0.1, 0.22, 0.14, 0.18, 0.18, 0.18].map((p) => p * usableWidth);
    // Helpers
    const centerText = (text: string, x: number, y: number, opts = {}) =>
      pdf.text(text, x, y, { align: "center", baseline: "middle", ...opts });
    const drawHeader = (first = false) => {
      const y = layout.headerY;
      pdf.setFontSize(22).setTextColor(202, 202, 202).text("MySIPonline", layout.marginX, y);
      if (first) {
        const title = "STP Report";
        pdf
          .setFontSize(18)
          .setTextColor(0)
          .text(title, pageWidth / 2, y, { align: "center" });
      }
    };
    const drawTableHeader = (y: number) => {
      let x = layout.marginX;
      headers.forEach((h, i) => {
        pdf.setFontSize(11).setTextColor(255, 255, 255).setFillColor(16, 188, 157).setDrawColor(217, 217, 217);
        pdf.rect(x, y, colWidths[i], layout.rowH, "FD"); // Fill + Border
        centerText(h, x + colWidths[i] / 2, y + layout.rowH / 2);
        x += colWidths[i];
      });
    };
    const drawRow = (y: number, values: (string | number)[]) => {
      let x = layout.marginX;
      pdf.setFontSize(10).setTextColor(99, 89, 90).setDrawColor(217, 217, 217);
      values.forEach((val, i) => {
        pdf.rect(x, y, colWidths[i], layout.rowH);
        centerText(String(val), x + colWidths[i] / 2, y + layout.rowH / 2);
        x += colWidths[i];
      });
    };
    const drawFooter = (pages: number) => {
      for (let p = 1; p <= pages; p++) {
        pdf.setPage(p);
        const footerY = pageHeight - 8;
        pdf.setFontSize(12).setTextColor(0).text(`Page ${p}/${pages}`, layout.marginX, footerY);
        const prefix = "Report by ";
        const w = pdf.getTextWidth(prefix);
        pdf.text(prefix, pageWidth - layout.marginX - w - 40, footerY);
        pdf
          .setTextColor(0, 102, 153)
          .textWithLink("MySIPonline", pageWidth - layout.marginX - 38, footerY, { url: "https://mysiponline.com" });
      }
    };
    // === Build PDF ===
    let y = layout.headerArea;
    drawHeader(true);
    drawTableHeader(y);
    y += layout.rowH;
    for (const row of calculation?.monthlyReport ?? []) {
      const values = [
        row.month,
        Number(row.remainBalance).toFixed(2),
        Number(row.stpPerMonth).toFixed(2),
        Number(row.liquidGrowth).toFixed(2),
        Number(row.equityGrowth).toFixed(2),
        Number(row.currentEquityValue).toFixed(2),
      ];
      if (y + layout.rowH > pageHeight - layout.footer) {
        pdf.addPage();
        drawHeader();
        y = layout.headerArea + 4;
        drawTableHeader(y);
        y += layout.rowH;
      }
      drawRow(y, values);
      y += layout.rowH;
    }
    drawFooter(pdf.internal.pages.length - 1);
    pdf.save("Stp_Report.pdf");
  };

  const handleScrollReport = () => {
    if (reportRef.current) {
      const headerOffset = 30;
      const elementPosition = reportRef.current.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value.replace(/,/g, "");

    if (!/^\d*$/.test(raw)) return;

    if (raw === "") {
      raw = "0";
      setError(true);
    }

    let num = Number(raw);
    const max = 10000;

    if (num > max) {
      num = max;
      raw = max.toString();
    }

    setError(num === 0);
    setStpAmount(raw);
  };

  const handleRightPart = () => {
    if (!isTab) return;
    setIsOpen(true);
  };

  const formattedAmount = stpAmount ? formatIndianNumber(Number(stpAmount)) : "";

  const displayedData = useMemo(() => {
    if (isLoggedIn) return calculation?.monthlyReport ?? [];
    const realRows = (calculation?.monthlyReport ?? []).slice(0, 2);
    const fakeCount = Math.max(5 - realRows.length, 0);
    const startYear = realRows.length > 0 ? realRows.length + 1 : 1;
    const fakeRows = Array.from({ length: fakeCount }, (_, i) => ({
      month: `${startYear + i}`,
      remainBalance: investmentAmount,
      stpPerMonth: 1000 + i,
      liquidGrowth: (10000 + i) / 25,
      equityGrowth: 858,
      currentEquityValue: 858 + i,
      isFake: true,
    }));
    return [...realRows, ...fakeRows];
  }, [isLoggedIn, calculation?.monthlyReport]);

  return (
    <>
      {/* Hero Heading */}
      <section className="section px-3 sm:px-6 lg:px-3 mb-12 sm:mb-100">
        {/* Floating Calculator */}
        <div ref={calcRef} className="relative w-full xs:bg-white xs:rounded-xl xs:shadow-lg sm:overflow-hidden xs:p-6">
          <div className="flex flex-col lg:flex-row gap-6 items-center w-full bg-white text-black rounded-xl shadow-lg xs:rounded-none xs:shadow-none p-4 sm:p-0 lg:h-154.5">
            {/* Inputs */}
            <div className="w-full lg:w-[40%] shrink-0">
              <div className="space-y-8 sm:space-y-12">
                <CalcInputValues
                  label="Investment Amount (Liquid Fund)"
                  min={50000}
                  max={10000000}
                  unit="₹"
                  amountValue={investmentAmount}
                  unitPosition="left"
                  onChange={setInvestmentAmount}
                  showFloatingValue
                />

                <CalcInputValues
                  label="Interest on Liquid Fund"
                  min={1}
                  max={10}
                  unit="%"
                  amountValue={liquidRate}
                  onChange={setLiquidRate}
                  allowDecimal
                />

                <CalcInputValues
                  label="Interest on Equity Fund"
                  min={1}
                  max={15}
                  unit="%"
                  amountValue={equityRate}
                  onChange={setEquityRate}
                  allowDecimal
                />

                <CalcInputValues
                  label="Duration (Months)"
                  min={1}
                  max={120}
                  unit="Months"
                  amountValue={months}
                  onChange={setMonths}
                />

                <div>
                  <label
                    htmlFor="stp-amount"
                    className="flex items-center gap-2 xs:gap-3 cursor-pointer font-semibold text-left text-2xs xxxs:text-sm sm:text-base lg:text-lg text-[#222222]"
                  >
                    <input
                      type="checkbox"
                      name="isCustomStpAmout"
                      id="stp-amount"
                      checked={isCheckboxActive}
                      onChange={(e) => setIsCheckboxActive(e.target.checked)}
                      className="w-3.5 h-3.5 xs:w-4 xs:h-4 accent-primaryGreen"
                    />
                    Want to Calculate with custom STP Amount ?
                  </label>

                  <AnimatePresence>
                    {isCheckboxActive && (
                      <motion.div
                        key="custom-stp-input"
                        initial={{ opacity: 0, height: 0, marginTop: 0 }}
                        animate={{ opacity: 1, height: "auto", marginTop: 15 }}
                        exit={{ opacity: 0, height: 0, marginTop: 0 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className={`w-full max-w-md relative flex justify-start rounded-lg overflow-hidden p-1.5 xs:py-2 xs:px-4 font-medium text-xs sm:text-sm lg:text-base transition border ${error ? "text-[#f90d0d] border-[#f90d0d] bg-[#fff3f3]" : "text-primaryGreen border-[#cad3e2] bg-white focus-within:border-[#a0a4ac]"}`}
                      >
                        <span
                          className={`${error ? "text-[#f90d0da4]" : "text-[#006b66a4]"} inline-block absolute top-1/2 left-[42%] xs:left-[43%] -translate-y-1/2 my-auto pointer-events-none`}
                        >
                          ₹
                        </span>
                        <input
                          type="text"
                          value={formattedAmount}
                          onChange={handleInputChange}
                          className="pl-[45%] w-full bg-inherit outline-hidden"
                          placeholder="Enter custom STP amount"
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              <button
                onClick={handleRightPart}
                className="block lg:hidden mt-4 w-full max-w-fit mx-auto bg-primaryGreen rounded-xl text-sm font-medium text-white px-8 xs:px-14 py-2 xs:py-2.5"
              >
                Calculate
              </button>
            </div>

            {!isTab && <RightPart className="hidden lg:block" calculation={calculation!} />}
          </div>
          {!isMob && <StartSIPStrip className="hidden sm:flex" />}
        </div>
      </section>

      <section className="section mb-half-px sm:hidden">{isMob && <StartSIPStrip className="sm:hidden" />}</section>

      {/* Detailed Report (margin auto from calc height) */}
      <section ref={reportRef} className="section mb-half-px sm:mb-100">
        <div className="flex items-center gap-3 mb-3">
          <h2 className="text-lg sm:text-2xl lg:text-3xl font-medium">Detailed Report</h2>

          <span className="ml-auto text-sm sm:text-base text-[#004b6b]">Download</span>

          <button
            onClick={handleExcelDownload}
            aria-label="Excel File Download Button"
            className="border border-[#56565640] rounded-lg px-3 py-2 hover:border-[#56565680]"
          >
            <Image
              src="https://www.mysiponline.com/images/swp-calculator/excel.svg"
              alt="strip-image"
              width={24}
              height={24}
            />
          </button>

          <button
            onClick={handlePDFDownload}
            aria-label="PDF Download Button"
            className="border border-[#56565640] rounded-lg px-3 py-2 hover:border-[#56565680]"
          >
            <Image
              src="https://www.mysiponline.com/images/swp-calculator/pdf.svg"
              alt="strip-image"
              width={24}
              height={24}
            />
          </button>
        </div>

        <div className="rounded-2xl overflow-hidden">
          <div
            className={`${isLoggedIn ? "overflow-auto" : "overflow-hidden"} relative rounded-2xl sm:px-5 max-h-141 scrollbar-xs sm:scrollbar-md border-2 border-primaryBlack`}
          >
            <table className="w-full text-left border-collapse">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-none">
                  <th className="p-0 sticky left-0 ">
                    <p className="p-4 whitespace-nowrap text-2xs sm:text-2sm lg:text-xl sm:border-b-2 border-primaryBlack font-semibold text-primaryBlack text-center bg-[#f2f2f2] sm:bg-white">
                      Months
                    </p>
                  </th>
                  <th className="p-0">
                    <p className="p-4 whitespace-nowrap text-2xs sm:text-2sm lg:text-xl sm:border-b-2 border-primaryBlack font-semibold text-primaryBlack text-center bg-[#f2f2f2] sm:bg-white">
                      Opening Balance
                    </p>
                  </th>
                  <th className="p-0">
                    <p className="p-4 whitespace-nowrap text-2xs sm:text-2sm lg:text-xl sm:border-b-2 border-primaryBlack font-semibold text-primaryBlack text-center bg-[#f2f2f2] sm:bg-white">
                      STP Amount
                    </p>
                  </th>
                  <th className="p-0">
                    <p className="p-4 whitespace-nowrap text-2xs sm:text-2sm lg:text-xl sm:border-b-2 border-primaryBlack font-semibold text-primaryBlack text-center bg-[#f2f2f2] sm:bg-white">
                      Liquid Returns
                    </p>
                  </th>
                  <th className="p-0">
                    <p className="p-4 whitespace-nowrap text-2xs sm:text-2sm lg:text-xl sm:border-b-2 border-primaryBlack font-semibold text-primaryBlack text-center bg-[#f2f2f2] sm:bg-white">
                      Equity Returns
                    </p>
                  </th>
                  <th className="p-0">
                    <p className="p-4 whitespace-nowrap text-2xs sm:text-2sm lg:text-xl sm:border-b-2 border-primaryBlack font-semibold text-primaryBlack text-center bg-[#f2f2f2] sm:bg-white">
                      Equity Value
                    </p>
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white">
                {displayedData.map((row) => (
                  <tr key={row.month} className="border-b border-[#cacaca] border-dashed last:border-0">
                    <td className="px-4 py-3 text-center text-xs sm:text-2sm text-primaryBlack sticky left-0 bg-[#f2f2f2] sm:bg-white">
                      {row.month}
                    </td>

                    <td className="px-4 py-3 text-center text-xs sm:text-2sm text-primaryBlack">
                      {formatIndianNumber(Number(row.remainBalance).toFixed(0), { prefix: "₹" })}
                    </td>

                    <td className="px-4 py-3 text-center text-xs sm:text-2sm text-primaryBlack">
                      {formatIndianNumber(Number(row.stpPerMonth).toFixed(0), { prefix: "₹" })}
                    </td>

                    <td className="px-4 py-3 text-center text-xs sm:text-2sm text-primaryBlack">
                      {formatIndianNumber(Number(row.liquidGrowth).toFixed(2), { prefix: "₹" })}
                    </td>

                    <td className="px-4 py-3 text-center text-xs sm:text-2sm text-primaryBlack">
                      {formatIndianNumber(Number(row.equityGrowth).toFixed(0), { prefix: "₹" })}
                    </td>

                    <td className="px-4 py-3 text-center text-xs sm:text-2sm text-primaryBlack">
                      {formatIndianNumber(Number(row.currentEquityValue).toFixed(0), { prefix: "₹" })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {!isLoggedIn && (
              <div className="absolute inset-0 top-20 bottom-1 xs:bottom-0 xs:top-24 z-1 mx-auto max-w-[98%] bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,rgba(255,255,255,0.39)_41.78%)] flex items-center justify-center text-center text-sm sm:text-base font-medium text-primaryBlack px-6">
                <div className="gradient-blur rounded-2xl">
                  <div /> <div /> <div /> <div /> <div /> <div />
                </div>

                <div className="relative z-1 bg-white xs:w-4/5 xl:w-2/3 h-fit border border-[#e6e6e6] shadow-[0px_4px_4px_0px_#00000026] flex flex-col sm:flex-row items-center p-4 xxxs:p-5 md:pl-10 rounded-md overflow-hidden gap-2 sm:gap-0">
                  <Image
                    className="absolute z-0 top-0 left-0 bottom-0"
                    src="https://www.mysiponline.com/images/swp-calculator/curves.svg"
                    alt="curves-image"
                    width={60}
                    height={50}
                  />
                  <p className="flex items-center gap-2 mx-auto xs:mx-0">
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 28 28"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="relative z-1 w-4 h-4 xs:w-7 xs:h-7"
                    >
                      <rect width="28" height="28" rx="14" fill="#1A51AA" />
                      <path
                        d="M14.3334 11.3334C13.6258 11.3334 12.952 11.474 12.3334 11.7246V9.3334C12.3334 8.22918 13.2292 7.3334 14.3334 7.3334C15.4382 7.3334 16.3334 8.22918 16.3334 9.3334V10H17.6668V9.3334C17.6668 7.49219 16.1746 6 14.3334 6C12.4922 6 11 7.49219 11 9.3334V12.5072C9.78199 13.4844 9 14.983 9 16.6666C9 19.612 11.388 22 14.3334 22C17.2788 22 19.6668 19.612 19.6668 16.6666C19.6668 13.7212 17.2788 11.3334 14.3334 11.3334ZM15 17.1484V18.6666H13.6668V17.1484C13.2696 16.918 13 16.4922 13 16C13 15.2642 13.597 14.6666 14.3334 14.6666C15.0698 14.6666 15.6668 15.2642 15.6668 16C15.6668 16.4922 15.3972 16.918 15 17.1484Z"
                        fill="white"
                      />
                    </svg>

                    <span className="whitespace-nowrap relative z-1 text-sm xs:text-base sm:text-lg text-primaryBlue font-medium">
                      Sign in to Unlock Premium Details
                    </span>
                  </p>
                  <button
                    onClick={handleLogin}
                    className="bg-transparent border-2 text-2xs sm:text-base border-primaryBlue text-primaryBlue rounded-full font-medium mx-auto sm:mr-0 sm:ml-auto py-1 px-10 w-fit transition duration-300 hover:bg-[#CAE7FF]"
                  >
                    Unlock Now
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {isTab && (
        <CustomModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          maxSize="max-w-[80%]"
          useDragToClose
          isBlurOverlay
          className="bg-white rounded-b-none sm:rounded-4xl"
        >
          <RightPart
            className="block lg:hidden"
            isOpen={isOpen}
            calculation={calculation!}
            onClose={() => setIsOpen(false)}
            onScrollToReport={handleScrollReport}
          />
        </CustomModal>
      )}
    </>
  );
}

function RightPart({
  className,
  isOpen,
  calculation,
  onClose,
  onScrollToReport,
}: {
  isOpen?: boolean;
  className?: string;
  calculation: StpCalculationResult;
  onClose?: () => void;
  onScrollToReport?: () => void;
}) {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const [isChartLoading, setIsChartLoading] = useState(true);

  const chartOptions: Highcharts.Options = {
    chart: {
      type: "column",
      height: 300,
      spacing: [24, 10, 10, 10],
      backgroundColor: "transparent",
      style: { fontFamily: "Inter, sans-serif" },
      events: {
        load: () => {
          setIsChartLoading(false);
        },
      },
    },
    title: { text: "" },
    xAxis: {
      categories: calculation?.monthArr?.map((m) => m.toString()) ?? [],
      title: { text: "Investment Period (Months)" },
      labels: {
        style: {
          color: "#202020",
          fontSize: "12px",
          fontWeight: "500",
        },
      },
    },
    yAxis: {
      title: {
        text: "Value of Investment (₹)",
        style: {
          color: "#202020",
          fontSize: "13px",
          fontWeight: "600",
        },
      },
      labels: {
        style: { color: "#555" },
      },
    },
    credits: { enabled: false },
    legend: {
      align: "right",
      verticalAlign: "top",
      layout: "horizontal",
      x: -10,
      y: -4,
      itemStyle: {
        color: "#222",
        fontWeight: "500",
      },
    },
    plotOptions: {
      series: {
        stacking: "normal",
        dataLabels: { enabled: false },
      },
      column: {
        borderRadius: 4,
        pointPadding: 0.1,
        groupPadding: 0.05,
      },
    },
    tooltip: {
      shared: true,
      useHTML: true,
      headerFormat: "<b>{point.key}</b><br>",
      pointFormat: '<span style="color:{series.color}">●</span> {series.name}: ₹{point.y:,.0f}<br>',
    },
    series: [
      {
        name: "Equity Fund Value",
        data: calculation?.equityAmtArr ?? [],
        color: "#2368b1",
        type: "column",
      },
      {
        name: "Liquid Fund Value",
        data: calculation?.liquidAmtArr ?? [],
        color: "#25b27d",
        type: "column",
      },
    ],
    responsive: {
      rules: [
        {
          condition: { maxWidth: 567 },
          chartOptions: {
            chart: { height: 260 },
            xAxis: {
              title: {
                style: { fontSize: "12px" },
              },
            },
            yAxis: {
              title: {
                style: {
                  fontSize: "11px",
                },
              },
              labels: {
                style: {
                  fontSize: "11px",
                },
              },
            },
            legend: {
              itemStyle: {
                fontSize: "12px",
              },
            },
          },
        },
      ],
    },
  };

  return (
    <>
      <div
        className={`w-full lg:w-[58%] lg:border-2 border-[#006b6633] rounded-2xl p-5 pt-0 sm:p-6 sm:pt-0 lg:p-6 bg-white sm:bg-[#f9fbfb] ${className}`}
      >
        <div className={`h-65 xs:h-300 ${isChartLoading ? "flex items-center justify-center" : ""}`}>
          {isChartLoading && (
            <Image
              src="/next_assets/images/gif/chart_loading.gif"
              unoptimized
              alt="Loading Chart Animation"
              loading="eager"
              width={256}
              height={144}
              priority
              className="w-64 h-36"
            />
          )}
          <HighchartsReact ref={chartRef} highcharts={Highcharts} options={chartOptions} />
        </div>

        <div className="bg-white px-0 sm:p-4 rounded-2xl mt-4 mx-auto sm:w-[90%] space-y-3.5 sm:space-y-5.5">
          <div
            className={`flex items-center justify-between text-2xs xs:text-2sm sm:text-lg font-bold ${calculation?.totalFutureValue < 0 ? "text-[#f90d0d]" : "text-primaryGreen"}`}
          >
            <p>Future Value</p>
            <p>
              {formatIndianNumber(Number(calculation?.totalFutureValue), { prefix: "₹" })}{" "}
              <span className="font-medium">({formatCompactNumber(calculation?.totalFutureValue ?? 0, true)})</span>
            </p>
          </div>
          <div className="flex items-center justify-between text-2xs xs:text-2sm sm:text-lg text-[#222222] font-semibold">
            <p>Total Investment</p>
            <p>
              {formatIndianNumber(Number(calculation?.totalInvestment), { prefix: "₹" })}{" "}
              <span className="font-medium text-[#808080]">
                ({formatCompactNumber(calculation?.totalInvestment ?? 0, true)})
              </span>
            </p>
          </div>
          <div className="flex items-center justify-between text-2xs xs:text-2sm sm:text-lg text-[#222222] font-semibold">
            <p>STP Per Month</p>
            <p>
              {formatIndianNumber(Number(calculation?.stpPerMonth), { prefix: "₹" })}{" "}
              <span className="font-medium text-[#808080]">
                ({formatCompactNumber(calculation?.stpPerMonth ?? 0, true)})
              </span>
            </p>
          </div>
          <div className="flex items-center justify-between text-2xs xs:text-2sm sm:text-lg text-[#222222] font-semibold">
            <p>Return On Liquid</p>
            <p>
              {formatIndianNumber(Number(calculation?.liquidReturn), { prefix: "₹" })}{" "}
              <span className="font-medium text-[#808080]">
                ({formatCompactNumber(calculation?.liquidReturn ?? 0, true)})
              </span>
            </p>
          </div>
          <div className="flex items-center justify-between text-2xs xs:text-2sm sm:text-lg text-[#222222] font-semibold">
            <p>Return on Equity</p>
            <p>
              {formatIndianNumber(Number(calculation?.equityReturn), { prefix: "₹" })}{" "}
              <span className="font-medium text-[#808080]">
                ({formatCompactNumber(calculation?.equityReturn ?? 0, true)})
              </span>
            </p>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="lg:hidden sticky bottom-0 flex items-center rounded-b-2xl justify-around gap-3 py-3 sm:py-5 bg-white border-t border-[#e4e4e4]">
          <a
            href={`${ROOT_URL}start-sip.php`}
            className="w-full max-w-fit mx-auto bg-primaryGreen rounded-xl border border-primaryGreen text-sm font-medium text-white px-6 xs:px-14 py-2 xs:py-2.5"
          >
            Start STP
          </a>
          <button
            onClick={() => {
              onClose?.();
              setTimeout(() => onScrollToReport?.(), 200);
            }}
            className="w-full max-w-fit mx-auto bg-white text-primaryGreen rounded-xl text-sm font-medium border border-primaryGreen px-5 xs:px-14 py-2 xs:py-2.5"
          >
            View Detail Report
          </button>
        </div>
      )}
    </>
  );
}
